import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import type { ProcessSection, RecruitmentInstanceStep, StepStatus } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { recruitmentService } from '@/services/recruitment.service';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { RightSheet, Avatar } from './shared';
import { sectionLabel, formatDate, fullName, getErrorMessage } from './utils';

export function ProcessDrawer({ instanceId, onClose }: { instanceId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.RECRUITMENT_MANAGE);
  // Only tracks locally-edited remarks; the server value is the fallback display.
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const q = useQuery({
    queryKey: ['recruitment', 'instance', instanceId],
    queryFn: () => recruitmentService.getInstance(instanceId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['recruitment', 'instance', instanceId] });
    void queryClient.invalidateQueries({ queryKey: ['recruitment', 'instances'] });
    void queryClient.invalidateQueries({ queryKey: ['recruitment', 'stats'] });
  };

  const toggle = useMutation({
    mutationFn: ({ stepId, status, remarks }: { stepId: string; status: StepStatus; remarks: string | null }) =>
      recruitmentService.toggleStep(instanceId, stepId, { status, remarks }),
    onSuccess: invalidate,
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const cancel = useMutation({
    mutationFn: (reason: string) => recruitmentService.cancelProcess(instanceId, reason),
    onSuccess: () => { toast.success('Process cancelled'); invalidate(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const inst = q.data;
  const isLocked = !canManage || inst?.status === 'cancelled' || inst?.status === 'completed';

  // Group steps by section in step order
  const grouped: { section: ProcessSection; steps: RecruitmentInstanceStep[] }[] = [];
  if (inst) {
    for (const s of inst.steps) {
      let g = grouped.find((x) => x.section === s.section);
      if (!g) { g = { section: s.section, steps: [] }; grouped.push(g); }
      g.steps.push(s);
    }
  }

  return (
    <>
    <RightSheet
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={inst ? fullName(inst.employee_first_name, inst.employee_last_name) : 'Process'}
      subtitle={inst?.employee_email}
      footer={
        inst && canManage && inst.status === 'in_progress' ? (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-destructive hover:underline"
            >
              <XCircle className="h-4 w-4" /> Cancel Process
            </button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </div>
        ) : (
          <div className="flex justify-end"><Button variant="outline" size="sm" onClick={onClose}>Close</Button></div>
        )
      }
    >
      {q.isLoading || !inst ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Avatar first={inst.employee_first_name} last={inst.employee_last_name} photoUrl={inst.photo_url} size="lg" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Overall Progress</span>
                <span className="font-medium text-foreground">
                  {inst.completed_steps}/{inst.total_steps} steps · {inst.progress_percentage}%
                </span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full rounded-full transition-all', inst.status === 'completed' ? 'bg-emerald-500' : 'bg-primary')}
                  style={{ width: `${inst.progress_percentage}%` }}
                />
              </div>
              {inst.joining_date && <p className="mt-1 text-xs text-muted-foreground">Joining: {formatDate(inst.joining_date)}</p>}
              {inst.status === 'cancelled' && <p className="mt-1 text-xs text-destructive">Cancelled · {inst.cancel_reason}</p>}
            </div>
          </div>

          {grouped.map((group) => {
            const done = group.steps.filter((s) => s.status === 'completed').length;
            return (
              <div key={group.section} className="rounded-lg border border-border/60">
                <div className="flex items-center justify-between border-b border-border/60 px-4 py-2.5">
                  <span className="text-sm font-semibold text-foreground">{sectionLabel(group.section)}</span>
                  <span className="text-xs text-muted-foreground">{done}/{group.steps.length} done</span>
                </div>
                <div className="divide-y divide-border/40">
                  {group.steps.map((s) => {
                    const completed = s.status === 'completed';
                    return (
                      <div key={s.id} className="px-4 py-3">
                        <div className="flex items-start gap-2.5">
                          <button
                            type="button"
                            disabled={isLocked}
                            onClick={() => toggle.mutate({ stepId: s.id, status: completed ? 'pending' : 'completed', remarks: edited[s.id] ?? s.remarks ?? null })}
                            className={cn('mt-0.5 shrink-0 transition-colors', isLocked && 'cursor-not-allowed opacity-60')}
                          >
                            {completed
                              ? <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                              : <Circle className="h-5 w-5 text-muted-foreground hover:text-primary" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={cn('text-sm font-medium', completed ? 'text-muted-foreground line-through' : 'text-foreground')}>
                                {s.step_name}
                              </span>
                              {s.is_required && (
                                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">Required</span>
                              )}
                              {s.is_sla_breached && !completed && (
                                <span className="rounded bg-rose-50 dark:bg-rose-900/20 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700 dark:text-rose-400">Overdue</span>
                              )}
                            </div>
                            {completed && s.completed_at && (
                              <p className="mt-0.5 flex items-center gap-1 text-[11px] text-emerald-600"><Check className="h-3 w-3" />{formatDate(s.completed_at)}</p>
                            )}
                            <textarea
                              rows={1}
                              disabled={isLocked}
                              placeholder="Add remarks (optional)…"
                              value={edited[s.id] ?? s.remarks ?? ''}
                              onChange={(e) => setEdited((r) => ({ ...r, [s.id]: e.target.value }))}
                              onBlur={() => {
                                const val = edited[s.id];
                                if (val !== undefined && val !== (s.remarks ?? '')) {
                                  toggle.mutate({ stepId: s.id, status: s.status, remarks: val || null });
                                }
                              }}
                              className="mt-2 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </RightSheet>

    <Dialog open={cancelOpen} onOpenChange={(o) => { if (!o) { setCancelOpen(false); setCancelReason(''); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Process</DialogTitle>
        </DialogHeader>
        <Textarea
          placeholder="Reason for cancelling this process…"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
          rows={3}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => { setCancelOpen(false); setCancelReason(''); }}>
            Keep Process
          </Button>
          <Button
            variant="destructive"
            disabled={!cancelReason.trim() || cancel.isPending}
            onClick={() => { cancel.mutate(cancelReason.trim()); setCancelOpen(false); setCancelReason(''); }}
          >
            {cancel.isPending ? 'Cancelling…' : 'Cancel Process'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
